import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { LabadminService } from 'src/app/labadmin/labadmin.service';
import { VitalHttpServices } from 'src/app/core/services/VitalHttpServices';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface Address {
  Id: number;
  AccountID: number;
  AddressLabel: string;
  Address1: string;
  Address2?: string;
  State: string;
  City: string;
  ZipCode: string;
  IsActive: number;
  Action: string;
  ModifiedBy: number;
  CreatedBy: number;
}

@Component({
  selector: 'app-manage-additional-address',
  templateUrl: './manage-additional-address.component.html',
  styleUrls: ['./manage-additional-address.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    MatFormFieldModule, 
    MatInputModule, 
    MatSelectModule, 
    MatButtonModule, 
    MatIconModule,
    MatAutocompleteModule,
    NgxMaskDirective,
    NgxMaskPipe,
    MatTooltipModule,
  ],
  providers: [
    provideNgxMask()
  ]
})
export class ManageAdditionalAddressComponent implements OnInit {
  // Form data using ngModel
  newAddress: Address = {
    Id: 0,
    AccountID: 0,
    AddressLabel: '',
    Address1: '',
    Address2: '',
    State: '',
    City: '',
    ZipCode: '',
    IsActive: 1,
    Action: '',
    ModifiedBy: +sessionStorage.getItem('Userid'),
    CreatedBy: +sessionStorage.getItem('Userid')
  };
  addressToBeEditedIndex: number;

  addresses: Address[] = [];
  filteredAddresses: Address[] = [];
  searchText: string = '';
  isEditMode: boolean = false;
  editingAddressId: number | null = null;
  selectedStateNameObject = { itemName: '', itemId: '', value: '', displayname: '' };
  optionToolTipText: string = '';
  isCityDisabled = false;
  isStateDisabled = false;
  showShimmerForField = false;
  addressSearchText: any = '';
  
  // change tracking
  originalAddress: Address | null = null;
  hasChanges: boolean = false;

  // Sample states for dropdown
  states = [];
  sourceApp: string;
  dbName: any;

  overflowMap: { [key: string]: boolean } = {};

  checkOverflow(el: HTMLElement, key: string) {
    this.overflowMap[key] = el.scrollWidth > el.clientWidth;
  }

  constructor(
    public dialogRef: MatDialogRef<ManageAdditionalAddressComponent>,
    private labadminService: LabadminService,
    private vitalHttpServices: VitalHttpServices,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    console.log('Received Data:', this.data);
  }

  async ngOnInit() {
    this.addresses = this.data?.['savedAddress']?.length ? this.data?.['savedAddress'] : [];
    // initialize snapshot for add mode
    this.originalAddress = this.createDefaultAddress();
    let checkLaunchUrl = window.location.href.split('/#/')[1];
    if (checkLaunchUrl.toString().toLocaleLowerCase() != "home") {
      this.sourceApp = 'VitalDx';
      this.dbName = this.labadminService.deploymentKey;
    }else{
      this.sourceApp = 'Admin';
      this.dbName = sessionStorage.getItem('DeploymentKey');
    }
    await this.getAllStates();
  
    this.filteredAddresses = [...this.addresses];
  }

  private createDefaultAddress(): Address {
    return {
      Id: 0,
      AccountID: 0,
      AddressLabel: '',
      Address1: '',
      Address2: '',
      State: '',
      City: '',
      ZipCode: '',
      IsActive: 1,
      Action: '',
      ModifiedBy:  +sessionStorage.getItem('Userid'), 
      CreatedBy:  +sessionStorage.getItem('Userid')
    };
  }

  onAddressChange(): void {
    const baseline = this.isEditMode && this.originalAddress ? this.originalAddress : this.createDefaultAddress();
    this.hasChanges = JSON.stringify({
      AddressLabel: this.newAddress.AddressLabel ?? '',
      Address1: this.newAddress.Address1 ?? '',
      Address2: this.newAddress.Address2 ?? '',
      State: this.newAddress.State ?? '',
      City: this.newAddress.City ?? '',
      ZipCode: this.newAddress.ZipCode ?? ''
    }) !== JSON.stringify({
      AddressLabel: baseline.AddressLabel ?? '',
      Address1: baseline.Address1 ?? '',
      Address2: baseline.Address2 ?? '',
      State: baseline.State ?? '',
      City: baseline.City ?? '',
      ZipCode: baseline.ZipCode ?? ''
    });
  }

  async getAllStates(): Promise<void> {
    try {
      const data: any  = await this.labadminService.getStates(this.dbName).toPromise();
        this.states = data?.map((i: any) => ({
          ...i,
          StateDisplayName: `${i?.Description?.trim()} (${i?.LookupValue?.trim()})`
        })) || [];
    } catch (error) {
      this.states = [];
    }
  }

  onSubmit(form: any): void {
    if (this.isFormValid()) {
      if (this.isDuplicateLabel()) {
        // this.snackBar.open('An address with the same label already exists. Please use a different label.', 'Close');
        this.snackBar?.open('An address with the same label already exists. Please use a different label.', null, {
          panelClass: ['warning-snackbar']
        });
        return;
      } 
      if (this.isEditMode) {
        // Update existing address
        const index = this.addresses.findIndex((addr, index) => index === this.addressToBeEditedIndex);
        if (index !== -1) {
          // Only update ModifiedBy if there are actual changes
          const updatedAddress = { 
            ...this.newAddress, 
            Id: this.editingAddressId,
            CreatedBy: sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid')! : -100
          };
          
          // Preserve original ModifiedBy if no changes detected
          if (!this.hasChanges) {
            updatedAddress.ModifiedBy = this.addresses[index].ModifiedBy;
            updatedAddress.CreatedBy = this.addresses[index].ModifiedBy;
          } else {
            updatedAddress.ModifiedBy = sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid')! : -100;
            updatedAddress.CreatedBy = sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid')! : -100;
          }
          
          this.addresses[index] = updatedAddress;
        }
      } else {
        // Add new address
        const newAddress: Address = {
          ...this.newAddress,
          Id: null, // Simple ID generation
          ModifiedBy: sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid') : -100,
          CreatedBy: sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid') : -100,
          Action: 'Create'
        };
        this.addresses.unshift(newAddress);
      }

      // After save, clear form and exit edit mode
      this.isEditMode = false;
      this.editingAddressId = null;
      this.addressToBeEditedIndex = null;
      this.originalAddress = this.createDefaultAddress();
      this.selectedStateNameObject = { itemId: '', itemName: '', value: '', displayname: '' };
      this.newAddress = this.createDefaultAddress();
      this.disableCityAndStateFields(false);
      if (form && typeof form.resetForm === 'function') {
        form.resetForm(this.newAddress);
      }
      this.hasChanges = false;

      this.addresses = this.addresses?.map((item: any) => ({
        ...item
      }));

      this.filteredAddresses = [...this.addresses];
    }
  }

  isFormValid(): boolean {
    return !!(this.newAddress.AddressLabel && this.newAddress.Address1 && 
              this.newAddress.State && this.newAddress.City && this.newAddress.ZipCode);
  }

  isDuplicateLabel(): boolean {
    const newLabel = this.newAddress?.AddressLabel?.trim().toLowerCase();
  
    return this.filteredAddresses?.some((address, index) =>
      index !== this.addressToBeEditedIndex && address?.AddressLabel?.trim().toLowerCase() === newLabel && (address?.IsActive === 1 || address?.IsActive)
    ) ?? false;
  }

  editAddress(address: Address, index: number, $event: any): void {
    $event.stopPropagation();
    this.addressToBeEditedIndex = index;
    this.isEditMode = true;
    this.editingAddressId = address.Id;
    this.originalAddress = { ...address };
    this.newAddress = { ...address };
    this.hasChanges = false;
    this.getStateAndCity(true);
  }

  deleteAddress(addressId: number, index: number, $event: any): void {
    // if (confirm('Are you sure you want to delete this address?')) {
      $event.stopPropagation();
      this.addresses[index]['IsActive'] = 0;
      this.addresses[index]['ModifiedBy'] = sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid')! : -100;
      this.addresses[index]['CreatedBy'] = sessionStorage.getItem('Userid') ? +sessionStorage.getItem('Userid')! : -100;
      this.filteredAddresses = [...this.addresses];
    // }
  }

  resetForm(form: any): void {
    const target = this.isEditMode && this.originalAddress
      ? this.originalAddress
      : this.createDefaultAddress();

    this.newAddress = { ...target };

    // Rebuild or clear state display and enable fields appropriately
    if (this.isEditMode && this.originalAddress) {
      const match: any = this.states?.find((s: any) => s.Description === this.newAddress.State || s.LookupValue === this.newAddress.State);
      this.selectedStateNameObject = {
        itemId: match?.Description || '',
        itemName: match?.Description || '',
        value: match?.Description || '', 
        displayname: match?.StateDisplayName
      };
    } else {
      this.selectedStateNameObject = { itemId: '', itemName: '', value: '', displayname: '' };
      this.disableCityAndStateFields(false);
      this.showShimmerForField = false;
    }

    if (form && typeof form.resetForm === 'function') {
      form.resetForm(this.newAddress);
    }
  }

  clearForm(form: any): void {
    // Fully clear form to defaults and enable city/state
    this.isEditMode = false;
    this.editingAddressId = null;
    this.addressToBeEditedIndex = null;
    this.originalAddress = this.createDefaultAddress();

    this.newAddress = this.createDefaultAddress();
    this.selectedStateNameObject = { itemId: '', itemName: '', value: '', displayname: '' };
    this.disableCityAndStateFields(false);
    this.showShimmerForField = false;
    this.hasChanges = false;

    if (form && typeof form.resetForm === 'function') {
      form.resetForm(this.newAddress);
    } else if (form?.reset) {
      form.reset();
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  saveAddress() {
    this.dialogRef.close(this.addresses);
  }

  onAutoCompleteInputChange($event: any, field: any) {
    this.selectedStateNameObject['value'] = null; // once we type something then we have to remove the data.
    this.selectedStateNameObject['itemId'] = null; // once we type something then we have to remove the data.
    this.onAddressChange();
  }

  checkValue() {
    let displayProperty = 'Description';
    setTimeout(() => {
      let fieldItem = this.states.find((item: any) => item[displayProperty] === this.selectedStateNameObject?.['itemName']);
      if(!fieldItem) {
        this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
      }
      this.onAddressChange();
    }, 500);
  }

  filterAutomCompleteOptions(autoCompleteInputboxValue: any, filterProperty: any) {
    if (!autoCompleteInputboxValue) {
      return this.states;
    }

    else {
      let filteredAutoCompleteOptions = this.states;
      return filteredAutoCompleteOptions.filter((option: any) => option[filterProperty]?.toLowerCase().includes(autoCompleteInputboxValue?.toLowerCase()));
    }
  }

  onAutoCompleteSelectionChange(e, particularObject) {
    if (e.source.selected) {
      this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
      let value = particularObject['Description'];
      let itemId = particularObject['Description'];
      let itemName = particularObject['Description'];
      let displayname = particularObject['StateDisplayName'];
      this.newAddress['State'] = particularObject['Description'];
      this.selectedStateNameObject = {...{ itemId: itemId, itemName: itemName, value: value, displayname: displayname }};
    } else {
      this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
    }
    document.dispatchEvent(new Event('click'));
    this.onAddressChange();
  }

  enableToolTip(e, type = '') {
    const text = e.scrollWidth > e.clientWidth ? e.textContent : '';
    this.optionToolTipText = text;
  }

  getStateAndCity(isFromEditCall = false) {
    this.showShimmerForField = true;
    const zipCode = this.newAddress.ZipCode;

    if(isFromEditCall && !zipCode) {
      this.newAddress.State = '';
      this.newAddress.City = '';
      this.showShimmerForField = false;
      this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
      return;
    }
    // Reset fields if zip code is invalid
    if (!zipCode || ![5, 10].includes(zipCode.length)) {
      this.disableCityAndStateFields(false);
      this.newAddress.State = '';
      this.newAddress.City = '';
      this.showShimmerForField = false;
      this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
      return;
    }

    // Get first 5 digits of zip code
    const fiveDigitZip = zipCode.substring(0, 5);
    
    this.labadminService.getStateAndCity(fiveDigitZip, this.labadminService.deploymentKey).subscribe({
      next: (data: any) => {
        if (data?.City && data?.State) {
          const matchingState: any = this.states.find((state: any) => state.LookupValue === data.State);
          if (matchingState) {
            // Patch form values
            this.newAddress['State'] = matchingState.Description;
            this.newAddress['City'] = data.City;
            this.selectedStateNameObject = { itemName: matchingState.Description, itemId: matchingState.Description, value: matchingState.Description, displayname: matchingState.StateDisplayName };

            this.disableCityAndStateFields(true);
          } else {
            if(!isFromEditCall) {
              console.error("State not found in the dropdown list.");
              this.disableCityAndStateFields(false);
              this.newAddress.State = '';
              this.newAddress.City = '';
              this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
            }
          }
          this.showShimmerForField = false;
        } else {
          if(!isFromEditCall) {
            console.error("No data or city found for the provided ZIP code.");
            this.disableCityAndStateFields(false);
            this.newAddress.State = '';
            this.newAddress.City = '';
            this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
          } else {
            const matchingState: any = this.states.find((state: any) => state.Description === this.newAddress.State);
            this.selectedStateNameObject = {...{ itemId: matchingState?.Description, itemName: matchingState?.Description, value: matchingState?.Description, displayname: matchingState?.StateDisplayName }};
            this.disableCityAndStateFields(false);
          }
          this.showShimmerForField = false;
        }
      },
      error: (error) => {
        if(!isFromEditCall) {
          console.error("Error fetching state and city:", error);
          this.disableCityAndStateFields(false);
          this.newAddress.State = '';
          this.newAddress.City = '';
          this.selectedStateNameObject = {...{ itemId: '', itemName: '', value: '', displayname: '' }};
        }
        this.showShimmerForField = false;
      }
    });    
  }

  disableCityAndStateFields(disable: boolean) {
    if (disable) {
      this.isCityDisabled = true;
      this.isStateDisabled = true;
    } else {
      this.isCityDisabled = false;
      this.isStateDisabled = false;
    }
  }

  isVisible(address: Address): boolean {
    if (address?.IsActive === 0 || !address?.IsActive) return false;
    const q = (this.addressSearchText ?? '').toString().trim().toLowerCase();
    if (!q) return true;
    const fullAddress = `${address.AddressLabel} ${address.Address1} ${address.Address2} ${address.City} ${address.State} ${address.ZipCode}`
      .toLowerCase();
    return fullAddress.includes(q);
  }

  getMessage() {
    const list = this.filteredAddresses ?? [];
    return list.some(item => this.isVisible(item));
  }

  onCityInput(event: any) {
    const input = event.target as HTMLInputElement;
    // Allow letters, spaces, hyphen, and apostrophe
    const cleaned = (input.value || '').replace(/[^a-zA-Z\s\-']/g, '');
    if (cleaned !== input.value) {
      input.value = cleaned;
      this.newAddress.City = cleaned;
    }
  }
}
